/* ref.control_adapter.js */

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const glueJay = require("@gluenetworks/gluejay").Gluejay;
const MultiComboBox = require("@gluenetworks/combobox").Multiple;
const SingleComboBox = require("@gluenetworks/combobox").Single;
const q = require("q");
const PickList = require("@gluenetworks/picklist").PickList;
const StyleUtil = require("../StyleUtil");

/**
 * @event RefControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class RefControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires RefControlAdapter#ValueChanged
 */
module.exports = function RefAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadedDeferred = q.defer();
    //call Event Emitter Constructor
    EventEmitter.call(this);
    var _scope;
    var _multiSelect;
    var _pickList;
    var _refTypeMode;
    var _pathOnlyMode;
    var _oldValue;
    var _oldInvalidStatus;
    var _validationMessages = [];
    /** @type {Object.<InstanceId,ref>} **/
    var _options = {};
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/
    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    function _valueChangedListener() {
        _fireValueChanged();
        self._updateValidityStatus();
    }

    /**
     * Fire the Value Changed Event
     * @private
     */
    function _fireValueChanged() {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    }

    function _fireInvalidStatusChanged() {
        let newInvalidStatus = self.isInvalid();
        if (newInvalidStatus !== _oldInvalidStatus) {
            self.emit("Invalid", {
                name: formCtrlDef.name,
                containingElem: self.containingElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            _oldInvalidStatus = newInvalidStatus;
        }
    }

    this._updateValidityStatus = function () {
        let newInvalidStatus = self.isInvalid();
        if (newInvalidStatus) {
            _validationMessages = ["This is a required field."];
            self._refContainer.classList.add("ref-invalid");
            self._errorsDiv.textContent = _validationMessages[0];
            self._errorsDiv.style.display = "block";
        } else {
            _validationMessages = [];
            self._refContainer.classList.remove("ref-invalid");
            self._errorsDiv.textContent = "";
            self._errorsDiv.style.display = "none";
        }
        _fireInvalidStatusChanged();
    };

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val;
        if (_refTypeMode) {
            if (_scope.selectList.getValue) {
                let optionPaths = _scope.selectList.getValue();
                if (_pathOnlyMode) {
                    if (_multiSelect) {
                        val = {
                            paths: [],
                        };
                        if (_.isArray(optionPaths)) {
                            optionPaths.forEach((optionPath) => {
                                val.paths.push(optionPath);
                            });
                        } else {
                            val.paths.push(optionPaths);
                        }
                    } else {
                        val = {};
                        if (_.isEmpty(optionPaths)) {
                            val.path = null;
                        } else {
                            val.path = optionPaths;
                        }
                    }
                } else {
                    let pathName = _.get(formCtrlDef, "configSettings.Ref.path", "Unknown");
                    console.warn(
                        `Could not get value for path ${pathName}. Type refs only supported with pathOnly true`
                    );
                }
            }
        } else {
            //if the _scope.selectList.getValue method is initialized retrieve the current value from that widget
            if (_scope.selectList.getValue) {
                var optionNames = _scope.selectList.getValue();
                if (_multiSelect) {
                    if (_.isArray(optionNames)) {
                        val = {"@ref": {instances: []}};
                        optionNames.forEach(function (id) {
                            if (_options.hasOwnProperty(id)) {
                                val["@ref"].instances.push(_options[id]);
                            }
                        });
                    } else if (_options.hasOwnProperty(optionNames)) {
                        val = {"@ref": {instances: [_options[optionNames]]}};
                    }
                } else {
                    if (_options.hasOwnProperty(optionNames)) {
                        val = {"@ref": {instance: _options[optionNames]}};
                        val["@ref"].subref = _options[optionNames].subref;
                    }
                }
            }
            //If the _scope.selectList.getValue method is not yet initialized use the default fcd value
            else {
                val = formCtrlDef.value;
            }
        }
        //If val is not set or is an empty array lets make it null
        if (!val || _.isEmpty(val)) {
            val = {"@ref": null};
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        if (_refTypeMode) {
            if (_.has(value, "path") || _.has(value, "paths")) {
                if (_multiSelect) {
                    if (_scope.selectList.setValue) {
                        _scope.selectList.setValue(value.paths);
                    }
                } else {
                    if (_scope.selectList.setValue) {
                        _scope.selectList.setValue(value.path);
                    }
                }
            } else {
                if (!_.isEmpty(value)) {
                    let pathName = _.get(formCtrlDef, "configSettings.Ref.path", "Unknown");
                    console.warn(
                        `Could not set value for path ${pathName}. Type refs only supported with pathOnly true`
                    );
                }
            }
        } else {
            var instanceId;
            if (value && value["@ref"]) {
                if (_multiSelect) {
                    if (value["@ref"].instances && _.isArray(value["@ref"].instances)) {
                        instanceId = [];
                        value["@ref"].instances.forEach(function (v, i) {
                            instanceId.push(v.instanceId);
                            if (_scope.selectList.setValue) {
                                var consistantVal;
                                if (!_options[v.instanceId]) {
                                    _options[v.instanceId] = {};
                                }
                                consistantVal = _options[v.instanceId];
                                //update the option with the latest info
                                _.set(
                                    consistantVal,
                                    "instanceId",
                                    _.get(value, "@ref.instances[" + i + "].instanceId")
                                );
                                _.set(
                                    consistantVal,
                                    "organizationId",
                                    _.get(value, "@ref.instances[" + i + "].organizationId")
                                );
                                _.set(
                                    consistantVal,
                                    "construct",
                                    _.get(value, "@ref.instances[" + i + "].construct")
                                );
                            }
                        });
                    }
                } else if (value["@ref"].instance) {
                    instanceId = value["@ref"].instance.instanceId;

                    //Update the choices in the select box
                    if (_scope.selectList.setValue) {
                        var consistantVal;
                        if (!_options[instanceId]) {
                            _options[instanceId] = {};
                        }
                        consistantVal = _options[instanceId];
                        //update the option with the latest info
                        _.set(
                            consistantVal,
                            "instanceId",
                            _.get(value, "@ref.instance.instanceId")
                        );
                        _.set(
                            consistantVal,
                            "organizationId",
                            _.get(value, "@ref.instance.organizationId")
                        );
                        _.set(consistantVal, "construct", _.get(value, "@ref.instance.construct"));
                    }
                }
                if (_scope.selectList.setValue) {
                    _scope.selectList.setValue(instanceId);
                }
            }
        }
        return self.getValue();
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            this._updateValidityStatus();
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    this.isInvalid = function () {
        let readOnlyInMultiEdit = this._multiEditMode && !this.getMultiEditActive();
        if (!this._required || readOnlyInMultiEdit) {
            return false;
        }
        let value = this.getValue();
        return _multiSelect ? _.isEmpty(value["@ref"].instances) : _.isNull(value["@ref"]);
    };

    this.getValidationMessages = function () {
        return _validationMessages.slice(0);
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        _scope.selectList.removeListener("Value", _scope.selectListChangeListener);
        _scope.selectList.removeListener("SelectionClick", _scope.setRefLaunchTitle);

        _scope.selectList.shutdown();
        //Do Nothing the dom is cleaned up, the scope is destroyed for us and any listeners
        //on self are unregistered automatically
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angular is before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @param $templateCache {$templateCache}
     */
    this.$init = function ($scope, $templateCache, $compile, $timeout) {
        glueJay.expandJtypeRefs(formCtrlDef.configSettings);
        _scope = $scope;

        if (formCtrlDef.configSettings.Ref.many) {
            _multiSelect = true;
        }

        if (_.has(formCtrlDef, "configSettings.Display.pickList")) {
            _multiSelect = true;
            _pickList = true;
        }

        if (_.has(formCtrlDef, "configSettings.Ref.constructs")) {
            _refTypeMode = true;
        }

        if (_.get(formCtrlDef, "configSettings.Ref.pathOnly", false)) {
            _pathOnlyMode = true;
        }

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        if (!_pickList) {
            StyleUtil.appendClassListToElement(
                $scope.jTypeContext.layout.inlineLabels,
                containingElem[0]
            );
        }

        //Get the options
        var options = [];

        $scope.types = [];
        if (_refTypeMode) {
            $scope.refTypeMode = true;
            if (formCtrlDef.refTypes) {
                let constructs = _.get(formCtrlDef, "configSettings.Ref.constructs", []);
                constructs.forEach((construct) => {
                    let refTypes = formCtrlDef.refTypes[construct.construct];
                    if (refTypes) {
                        $scope.refInfo = {
                            construct: construct.construct,
                        };
                        if (_pathOnlyMode) {
                            refTypes.forEach((refType) => {
                                $scope.types.push(_.get(refType, "displayName", refType.typeName));
                                _options[refType.id] = {
                                    typeId: refType.id,
                                    construct: construct.construct,
                                    organizationId: refType.organizationId,
                                    path: refType.path,
                                    typeName: _.get(refType, "displayName", refType.typeName),
                                };
                                let path = refType.path;

                                if (!path.endsWith(".jtype")) {
                                    path = path + ".jtype";
                                }
                                options.push({
                                    label: _.get(refType, "displayName", refType.typeName),
                                    value: path,
                                    description: refType.description,
                                });
                            });
                        } else {
                            let pathName = _.get(formCtrlDef, "configSettings.Ref.path", "Unknown");
                            console.warn(
                                `Could not initialize component for path ${pathName}. Type refs only supported with pathOnly true`
                            );
                        }
                    }
                });
            }
        } else {
            //If there are instances
            if (formCtrlDef.refInstances) {
                if (formCtrlDef.configSettings.Ref.types) {
                    formCtrlDef.configSettings.Ref.types.forEach(function (type) {
                        var refInstances = formCtrlDef.refInstances[type.typePath];
                        if (refInstances) {
                            $scope.refInfo = {
                                construct: type.construct,
                            };
                            $scope.types.push(_.get(type, "displayName", type.typeName));
                            refInstances.forEach(function (refInst) {
                                _options[refInst.id] = {
                                    instanceId: refInst.id,
                                    construct: type.construct,
                                    organizationId: refInst.organizationId,
                                    subref: type.subref,
                                    type: _.get(type, "displayName", type.typeName),
                                    instanceName: refInst.instanceName,
                                    labPath: refInst.labPath,
                                };
                                options.push({
                                    label: refInst.instanceName,
                                    value: refInst.id,
                                    description: refInst.description,
                                });
                            });
                        }
                    });
                }
            }
        }

        options.sort((a, b) => {
            if (a.label === b.label) {
                return 0;
            } else if (a.label > b.label) {
                return 1;
            } else {
                return -1;
            }
        });

        //Check if required field in validation object - if so, then do not allow user to select *none*
        if (formCtrlDef.validationInfo && formCtrlDef.validationInfo.required) {
            this._required = true;
        }

        // Setup Settings Object
        if (_pickList) {
            let configOptions = {
                optionsLabel: formCtrlDef.configSettings.Display.pickList.optionsLabel,
                selectedLabel: formCtrlDef.configSettings.Display.pickList.selectedLabel,
                verticalLayout: formCtrlDef.configSettings.Display.pickList.verticalLayout,
                hideOptionFilter: formCtrlDef.configSettings.Display.pickList.hideOptionFilter,
            };
            $scope.selectList = new PickList(configOptions);
        } else {
            if (_multiSelect) {
                $scope.selectList = new MultiComboBox({
                    allowClear: true,
                });
            } else {
                $scope.selectList = new SingleComboBox({
                    allowClear: true,
                });
            }
            let placeHolderText = "No Instances Selected";
            if (_refTypeMode) {
                placeHolderText = "No Types Selected";
            }
            if (_.isEmpty(options)) {
                placeHolderText = "No Instances Available";
            }
            if (_refTypeMode) {
                if (_.isEmpty(options)) {
                    placeHolderText = "No Types Available";
                } else {
                    placeHolderText = "No Types Selected";
                }
            }
            if (_.has(formCtrlDef, "configSettings.Display._multiEdit.diffs")) {
                placeHolderText = "Various";
            }

            $scope.selectList.setPlaceholder(placeHolderText);
        }
        $scope.selectListChangeListener = (newVal) => {
            _valueChangedListener();
            $scope.setRefLaunchTitle();
        };
        $scope.setRefLaunchTitle = () => {
            "use strict";
            let selectedId = $scope.selectList.getValue();
            // Check to see if the list has as a function
            if (_pickList) {
                let selected = $scope.selectList.getSelectionHighlight();
                if (!_.isEmpty(selected)) {
                    selectedId = selected;
                }
            }
            let launchIcon = containingElem.find(".launch-ref");
            if (launchIcon.length) {
                let setTooltip = (typeArray, nameType) => {
                    if (_.get($scope, "refInfo.construct")) {
                        if (typeArray.length > 1) {
                            let allTypeNames = [].concat(typeArray);
                            if (allTypeNames.length > 4) {
                                let truncCount = allTypeNames.length - 3;
                                allTypeNames.splice(3, truncCount, "...");
                            }
                            allTypeNames = allTypeNames.join("\n");
                            launchIcon[0].setAttribute(
                                "title",
                                `Go to \n${allTypeNames}\n${nameType}s`
                            );
                        } else {
                            launchIcon[0].setAttribute(
                                "title",
                                `Go to \n${typeArray[0]}\n${nameType}`
                            );
                        }
                    }
                };
                if (!_.isEmpty(selectedId)) {
                    if (_.isArray(selectedId)) {
                        let selectedTypes = [];
                        selectedId.forEach((id) => {
                            let option = _.get(_options, id, false);
                            if (option) {
                                if (_.isObject(option)) {
                                    option = _.get(option, "instanceName", "");
                                }
                                selectedTypes.push(option);
                            }
                        });
                        setTooltip(selectedTypes, "instance");
                    } else {
                        let option = _.get(_options, selectedId + ".instanceName", "Referenced");
                        setTooltip([option], "instance");
                    }
                } else {
                    setTooltip($scope.types, "type");
                }
            } else {
                containingElem[0].classList.add("no_launch");
            }
            return true;
        };
        $scope.launchRef = () => {
            "use strict";

            let buildFilterString = (typesArray) => {
                let terms = typesArray.map((typeName) => `Type eq ${typeName}`);
                return terms.join(" or ");
            };

            let goToItems = [];
            let launchObj = _.assign({}, $scope.refInfo);
            let selectedTypes = [];
            if (!_refTypeMode) {
                goToItems = $scope.selectList.getValue();
                if (!_.isEmpty(goToItems) && !_.isArray(goToItems)) {
                    goToItems = [goToItems];
                }
                // Check to see if the list has as a function
                if (_pickList) {
                    let selected = $scope.selectList.getSelectionHighlight();
                    if (!_.isEmpty(selected)) {
                        goToItems = selected;
                    }
                }
            }

            if (!_.isEmpty(goToItems)) {
                launchObj.instanceIds = [];
                launchObj.instances = [];

                goToItems.forEach((id) => {
                    let ref = _options[id];
                    launchObj.instanceIds.push(ref.instanceId);
                    launchObj.instances.push({
                        instanceId: ref.instanceId,
                        instanceName: _options[ref.instanceId].instanceName,
                    });
                    selectedTypes.push(ref.type);
                });
                selectedTypes = _.uniq(selectedTypes);
                if (selectedTypes.length > 1) {
                    launchObj.filter = buildFilterString(selectedTypes);
                } else if (selectedTypes.length === 1) {
                    launchObj.type = selectedTypes[0];
                    launchObj.filter = "";
                }
            } else {
                if ($scope.types.length > 1) {
                    launchObj.filter = buildFilterString($scope.types);
                } else {
                    launchObj.type = $scope.types[0];
                    launchObj.filter = "";
                }
            }

            $scope.$emit("instance.launchRef", launchObj);
            $scope.$emit("RefLaunched", {
                launchObj: launchObj,
                configData: formCtrlDef.configData,
                configSettings: formCtrlDef.configSettings,
                refInstances: formCtrlDef.refInstances,
            });
        };

        $scope.selectList.on("Value", $scope.selectListChangeListener);
        $scope.selectList.on("SelectionClick", $scope.setRefLaunchTitle);
        $scope.selectList.setOptions(options);

        //Setup directive
        var template = $templateCache.get("templatecache/templates/ref.template.html");
        var refElem = $(template);
        this._errorsDiv = $("<div class='ref-errors'></div>")[0];
        if (_pickList) {
            let divElem = $(
                '<div class="nested_rendered_controls object_control form_entry"></div>'
            );
            divElem.append(refElem);
            refElem = divElem;
        }

        this._refContainer = refElem[0];

        const isCredential =
            _.get(formCtrlDef.configSettings, "Ref.path", "").indexOf("secret/sys/credentials") >
            -1;
        const iconElement = this._refContainer.querySelector("launch-default-icon-gl");
        if (isCredential && iconElement) {
            iconElement.remove();
        }

        containingElem.append(refElem);
        if (_pickList) {
            containingElem.append(this._errorsDiv);
        } else {
            refElem.append(this._errorsDiv);
        }
        let optionContainer = containingElem.find(".ref-container");
        optionContainer.prepend(_scope.selectList.renderer.rootElement);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadedDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache", "$compile", "$timeout"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope, $timeout) {
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }
        $scope.setRefLaunchTitle();
        if (_pickList) {
            if (_.has(formCtrlDef.configSettings, "Display.pickList.lineCount")) {
                $scope.selectList.lineCount = _.get(
                    formCtrlDef.configSettings,
                    "Display.pickList.lineCount"
                );
            } else if (_.has(formCtrlDef.configSettings, "Display.pickList.minLineCount")) {
                $scope.selectList.lineCount = _.get(
                    formCtrlDef.configSettings,
                    "Display.pickList.minLineCount"
                );
            }
            // This is a hack to reset the max size of the picklist based on the current size of the view
            // area.
            if (_.isFunction(ResizeObserver)) {
                new ResizeObserver(() => {
                    let renderer = $scope.selectList.renderer;
                    if (renderer) {
                        $scope.selectList.renderer.rootElement.style.cssText =
                            "max-width: " + (containingElem[0].offsetWidth - 30) + "px";
                    }
                }).observe(containingElem[0]);
            }
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        if (formCtrlDef.configData) {
            //Update used/available based on configData
            self.setValue(formCtrlDef.configData);
            $timeout(function () {
                loadedDeferred.resolve();
            });
        } else {
            this._updateValidityStatus();
            loadedDeferred.resolve();
        }
    };
    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/

    this.setReadOnly = function (flag) {
        if (_.isFunction(_scope.selectList.setDisabled)) {
            _scope.selectList.setDisabled(flag);
        }
    };
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
